import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationAccuracyChart = ({ dateRange }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const mockData = [
    { month: 'Jan', overall: 82, sciences: 85, humanities: 78, business: 80, arts: 79 },
    { month: 'Fév', overall: 83, sciences: 86, humanities: 79, business: 82, arts: 81 },
    { month: 'Mar', overall: 85, sciences: 88, humanities: 81, business: 83, arts: 82 },
    { month: 'Avr', overall: 84, sciences: 87, humanities: 80, business: 84, arts: 83 },
    { month: 'Mai', overall: 86, sciences: 89, humanities: 82, business: 85, arts: 84 },
    { month: 'Juin', overall: 87.5, sciences: 90, humanities: 84, business: 86, arts: 85 }
  ];

  const categories = [
    { value: 'all', label: 'Toutes catégories', color: '#2563EB' },
    { value: 'sciences', label: 'Sciences', color: '#10B981' },
    { value: 'humanities', label: 'Sciences Humaines', color: '#F59E0B' },
    { value: 'business', label: 'Commerce', color: '#8B5CF6' },
    { value: 'arts', label: 'Arts', color: '#EC4899' }
  ];

  const getVisibleLines = () => {
    if (selectedCategory === 'all') {
      return ['overall'];
    }
    return [selectedCategory];
  };

  const getLineColor = (dataKey) => {
    const category = categories?.find(c => c?.value === dataKey);
    return category?.color || '#2563EB';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Target" size={24} className="text-primary" />
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Tendances de Précision des Recommandations
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              Évolution du taux de satisfaction des recommandations par filière
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories?.map((cat) => (
            <Button
              key={cat?.value}
              variant={selectedCategory === cat?.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat?.value)}
            >
              {cat?.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="h-80 md:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={mockData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              domain={[70, 95]}
              label={{ value: 'Précision (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value) => [`${value}%`, 'Précision']}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value) => {
                const labels = {
                  overall: 'Global',
                  sciences: 'Sciences',
                  humanities: 'Sciences Humaines',
                  business: 'Commerce',
                  arts: 'Arts'
                };
                return labels?.[value] || value;
              }}
            />
            {getVisibleLines()?.map((lineKey) => (
              <Line
                key={lineKey}
                type="monotone"
                dataKey={lineKey}
                stroke={getLineColor(lineKey)}
                strokeWidth={3}
                dot={{ fill: getLineColor(lineKey), r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-primary mb-1">87.5%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Précision Actuelle</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-success mb-1">+5.2%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Amélioration</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">90%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Meilleure Catégorie</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">2,847</p>
          <p className="text-xs md:text-sm text-muted-foreground">Évaluations</p>
        </div>
      </div>
    </div>
  );
};

export default RecommendationAccuracyChart;